import React, { useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { LeverageInstrumentDto } from 'api/Trading-Accounts/Types/InstrumentDto'
import { PlatformTypeDto } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { ServerType } from 'api/Trading-Accounts/Types/ServerType'
import { UpdateLeverageInstrument } from 'api/Trading-Accounts/Types/UpdateLeverageInstrument'
import AddButton from 'components/AddButton/AddButton'
import { Box } from 'components/Box'
import { InputFilterCell, selectFilterCell } from 'components/Filter/FilterCell'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { normalizeFilter } from 'features/DataGrid/helpers'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchTradingAccountsLeverageTypesAction } from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import stringComparator from 'utils/stringComparator'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { SourceServerForm } from './forms/SourceServerForm'
import { LeverageEditPage } from './LeverageEditPage'
import { UploadLeverageModal } from './UploadLeverageModal'

const useMapStateToProps = () => ({
  tradingAccountInstrumentTypes: useAppSelector(
    (state) => state.tradingAccounts.tradingAccountInstrumentType
  ),
  tradingAccountLeverageTypes: useAppSelector((state) => state.tradingAccounts.leverages),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  return {
    fetchTradingAccountLeverageTypes: async () =>
      dispatch(fetchTradingAccountsLeverageTypesAction(tradingAccountsApiClient)),
  }
}

const initialModalState = { data: undefined, visible: false }

interface ModalProps {
  visible: boolean
  data?: LeverageInstrumentDto
}

export const LeveragesPage: React.FC = () => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const [uploadLeverageModal, setUploadLeverageModal] = useState(false)
  const platformTypes = useAppSelector((state) => state.tradingAccounts.platformTypes)

  const [platform, setPlatformType] = useState<PlatformTypeDto | undefined>(undefined)
  const [server, setServer] = useState<ServerType | undefined>(undefined)
  const [platformServers, setPlatformServers] = useState<ServerType[]>([])

  const {
    tradingAccountLeverageTypes: leverageTypes,
    tradingAccountInstrumentTypes: instrumentTypes,
  } = useMapStateToProps()
  const { fetchTradingAccountLeverageTypes } = useMapDispatchToProps()

  const [auditLogModal, setAuditLogModal] = useState<ModalProps>(initialModalState)

  const handleAuditLogOpen = (record: LeverageInstrumentDto) => {
    setAuditLogModal({ data: record, visible: true })
  }

  const handleAuditLogClose = () => {
    setAuditLogModal({
      data: undefined,
      visible: false,
    })
  }

  useEffect(() => {
    if (platformTypes.length && !platform) {
      setPlatformType(platformTypes[0])
    }
  }, [platformTypes, platform])

  const [editLeverageModal, setEditLeverageModal] = useState<{
    data?: LeverageInstrumentDto
    visible: boolean
  }>({
    visible: false,
  })

  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<{ skip: number; take: number }>({
    ...getPaginationInitialState(),
    take: 1000,
  })

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (server && platform) {
      return tradingAccountsApiClient.getLeverages({
        platformId: platform?.id,
        serverId: server?.id,
      })
    }
  }, [server, platform])
  const { data, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    if (isEmpty(leverageTypes)) {
      fetchTradingAccountLeverageTypes()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (event: GridPageChangeEvent) => {
    if (data && data.length >= 1) {
      setPaginationState({ skip: event.page.skip, take: event.page.take })
    }
  }

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
    setPaginationState({
      ...getPaginationInitialState(),
      skip: 0,
      take: paginationState.take,
    })
  }

  const handleEditLeverage = (leverageType?: LeverageInstrumentDto) => {
    setEditLeverageModal({
      visible: true,
      data: leverageType,
    })
  }

  const onUpdateLeverageInstrument = async (values: UpdateLeverageInstrument) => {
    if (server && platform) {
      if (values?.id && values.leverageId) {
        await tradingAccountsApiClient.updateLeverageInstrument({
          platformId: platform.id,
          serverId: server.id,
          data: [
            {
              id: values.id,
              leverageTypeId: values.leverageId,
            },
          ],
        })
      }
      setEditLeverageModal({ visible: false, data: undefined })
      setToast(successToast('Leverage instrument is updated'))

      forceRefresh()
    }
  }

  const handleDownloadLeverageConfigurations = async () => {
    if (server && platform) {
      const leverageData = await tradingAccountsApiClient.getLeverages({
        platformId: platform.id,
        serverId: server.id,
      })

      const filteredData = filterBy(leverageData, filter)
      const sortedData = filteredData.sort((a, b) => stringComparator(a.symbol, b.symbol))

      const csvContent = sortedData
        .map((item) => {
          const symbol = item?.symbol || ''
          const leverage = item?.leverageType?.name || 'N/A'

          return `${symbol},${leverage}`
        })
        .join('\n')

      const csvHeader = 'Symbol,Leverage\n'
      const finalCsv = csvHeader + csvContent

      const blob = new Blob([finalCsv], { type: 'text/csv;charset=utf-8;' })
      const objUrl = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = objUrl
      link.setAttribute('download', 'leverage_configurations.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()

      URL.revokeObjectURL(objUrl)
    }
  }

  const gridData = filterBy(data || [], filter).sort((a, b) => stringComparator(a.symbol, b.symbol))

  const leverageTypesNoDuplicates = leverageTypes.filter((type) =>
    data?.find((item) => item?.leverageType?.id === type.id)
  )

  const InstrumentFilterCell = (props: GridFilterCellProps) => {
    return selectFilterCell({
      items: instrumentTypes.map((x) => ({
        id: x.id.toString(),
        value: x.id.toString(),
        label: x.name,
      })),
      options: {
        operator: '==',
      },
    })(props)
  }

  const LeverageFilterCell = (props: GridFilterCellProps) => {
    const getAllLeverageTypes = leverageTypesNoDuplicates?.map((x) => ({
      id: x.id,
      value: x.id,
      label: x.name,
    }))

    return selectFilterCell({
      items: getAllLeverageTypes,
      options: {
        operator: '==',
      },
    })(props)
  }

  return (
    <>
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          tradingAccountLeverageId={auditLogModal.data.id.toString()}
          isVisible={auditLogModal.visible}
          onClose={handleAuditLogClose}
        />
      )}
      {uploadLeverageModal && (
        <UploadLeverageModal
          leverageTypes={leverageTypes}
          platformId={platform?.id}
          serverId={server?.id}
          permissions={permissions}
          onClose={() => {
            setUploadLeverageModal(false)
            forceRefresh()
          }}
        />
      )}
      {editLeverageModal?.visible && (
        <LeverageEditPage
          isOpen={editLeverageModal.visible}
          onClose={() => setEditLeverageModal({ visible: false, data: undefined })}
          leverage={editLeverageModal.data}
          onUpdateLeverageInstrument={onUpdateLeverageInstrument}
        />
      )}
      <Breadcrumbs
        crumbs={[{ title: 'Leverage Configurations', link: links.brokerageLeverages.url }]}
      />

      <SourceServerForm
        platformServers={platformServers}
        setPlatformServers={setPlatformServers}
        server={server}
        setServer={setServer}
        platform={platform}
        setPlatformType={setPlatformType}
      />

      <Box display='flex' flexDirection='row' marginBottom={10}>
        <AddButton text='Upload CSV' onClick={() => setUploadLeverageModal(true)} />
        <AddButton
          text='Download'
          disabled={
            !gridData.slice(paginationState.skip, paginationState.take + paginationState.skip)
              .length
          }
          onClick={handleDownloadLeverageConfigurations}
        />
      </Box>

      <Loading isLoading={isLoading}>
        <Grid
          resizable
          filterable
          data={gridData.slice(paginationState.skip, paginationState.take + paginationState.skip)}
          filter={filter}
          skip={paginationState.skip}
          take={paginationState.take}
          total={gridData.length}
          onPageChange={handlePageChange}
          onFilterChange={handleFilterChange}
          pageable={{ pageSizes: [500, 750, 1000, 2000] }}
          data-test='table'
        >
          <Column field='symbol' title='Symbol' filterCell={InputFilterCell} />
          <Column field='name' title='Name' filterCell={InputFilterCell} />
          <Column
            field='type.id'
            title='Type'
            cell={(gridCellProps) => {
              const { type } = gridCellProps.dataItem
              const foundObject = instrumentTypes.find((i) => {
                return i.id === type.id
              })
              return <td>{foundObject?.name}</td>
            }}
            filterCell={InstrumentFilterCell}
          />
          <Column
            field='leverageType.id'
            cell={(gridCellProps) => {
              const { leverageType } = gridCellProps.dataItem

              const foundLeverage = leverageTypes?.find((type) => {
                return type.id === leverageType?.id
              })

              return <td>{foundLeverage?.name}</td>
            }}
            title='Leverage'
            filterCell={LeverageFilterCell}
          />
          <Column
            title=''
            width={50}
            filterable={false}
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      disabled: !hasPermission(permissions, PermissionsEnum.CanEditInstrument),
                      onClick: () => handleEditLeverage(gridCellProps.dataItem),
                    },
                    {
                      name: 'Audit Log',
                      'data-test': 'audit-log',
                      onClick: () => handleAuditLogOpen(gridCellProps.dataItem),
                    },
                  ]}
                />
              </td>
            )}
          />
        </Grid>
      </Loading>
    </>
  )
}
